/**
 * The client half of the duplicate-add defence.
 *
 * The disabled button is the visible part and the least important part. What
 * matters here is the idempotency key, and specifically that it is minted once
 * per intent - one tap of "Add to cart" - and reused for every retry of that
 * tap. A key regenerated per fetch would satisfy the type signature, pass a
 * casual reading, and defeat the entire mechanism: the server would see two
 * different keys and correctly perform two additions.
 *
 * So the key lives in this tracker, not inside the fetch helper. `begin` is what
 * a tap calls; a second tap while the first is in flight gets `null` and does
 * nothing, which is the honest version of a disabled button - it holds even when
 * the button was not disabled in time, when the customer hit Enter twice, and
 * when a second tab is open on the same listing.
 */

/** Matches IDEMPOTENCY_KEY_PATTERN in @tkm/domain: 16-128 of [A-Za-z0-9_-]. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID().replace(/-/g, '')
}

export interface AddAttempt {
  listingId: string
  idempotencyKey: string
}

/**
 * One in-flight add at a time, per listing.
 *
 * Deliberately not "one in flight globally": a customer browsing fast may
 * legitimately add two different books before the first response lands, and
 * blocking that would be a bug wearing a safety badge.
 */
export function createAddTracker() {
  const inFlight = new Map<string, string>()

  return {
    /** Null means "already adding this one" - the caller does nothing at all. */
    begin(listingId: string): AddAttempt | null {
      if (inFlight.has(listingId)) return null
      const idempotencyKey = newIdempotencyKey()
      inFlight.set(listingId, idempotencyKey)
      return { listingId, idempotencyKey }
    },

    /**
     * The key for a retry of the attempt already under way. The same key, which
     * is the whole point - a network failure must be retried as a replay, never
     * as a second addition.
     */
    keyFor(listingId: string): string | null {
      return inFlight.get(listingId) ?? null
    },

    /** Called on success AND on failure: a key that is never released would make
     * the listing permanently unaddable after one dropped connection. */
    settle(listingId: string): void {
      inFlight.delete(listingId)
    },

    adding(listingId: string): boolean {
      return inFlight.has(listingId)
    },
  }
}

export type AddTracker = ReturnType<typeof createAddTracker>
